import React, { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import PageHeader from "Components/PageHeader";
import { Typography } from "antd";
import { FileExcelOutlined } from "@ant-design/icons";
import { SbTable, SbTag, SbButton } from "uiCore";
import { PageTransitionStyled } from "./talentMobility.animations";
import {
  InfoCardStyled,
  InfoStatStyled,
  ReductionTableWrapperStyled,
} from "./RedeploymentReductionDetailV2/RedeploymentReductionDetailV2.styles";
import { IndexInfoLabel } from "./components/IndexTooltip";
import { buildPersonColumns, buildScoreColumn } from "./RedeploymentCenterV2/RedeploymentCenterV2.utils";
import { useMatchThresholds } from "Store/useTalentMobilitySettingsStore";
import { useWorkforcePeopleAvailability } from "./useTalentMobilityData";
import usePaicRedesignPlanStore from "Store/usePaicRedesignPlanStore";
import { exportRowsAsCsv } from "Utils/exportCsv.utils";
import { tableEmptyLocale } from "./ViewPlanningV2/ViewPlanningV2.utils";

const { Text } = Typography;

const NO_SHORTLIST = [];
const bySmiDesc = (a, b) => (b.sri ?? -Infinity) - (a.sri ?? -Infinity);

// PAIC Org Redesign ▸ Roles Added ▸ role — the people who could FILL an
// INCREASED role. A growing role has no one to reduce, so instead of
// Reduction Detail's occupant ranking this lists the role's candidates from
// GET /talent-mobility/workforce-planning/people-availability (Workforce
// Planning's own data source): everyone profiled against the role and not
// already in it, ranked by SMI.
//
// Each row's "View Profile / Shortlist" opens the person's Candidate Profile
// for this role (?flow=paic, same page Workforce Planning's candidates open),
// whose Shortlist writes straight into the PAIC plan draft — the role's
// available_employee_targets, each targeted at this role — so the header's
// Save / Update Plan buttons store the picks under the plan's
// roles_increased. Same page layout as Reduction Detail.
const PaicRoleCandidatesV2 = ({
  versionId,
  roleKey,
  roleId,
  title,
  currentPositions,
  targetPositions,
  onBack,
  planTag,
  planActions,
}) => {
  const navigate = useNavigate();
  const thresholds = useMatchThresholds();
  const role = useMemo(() => ({ id: roleId }), [roleId]);
  const { candidates, isLoading } = useWorkforcePeopleAvailability(role);

  const shortlist =
    usePaicRedesignPlanStore((state) => state.versions[versionId]?.roles[roleKey]?.available_employee_targets) ||
    NO_SHORTLIST;
  const shortlistedIds = useMemo(() => new Set(shortlist.map((entry) => entry.employee_unique_id)), [shortlist]);

  const openProfile = useCallback(
    (personId) => {
      const params = new URLSearchParams({ index: "smi", flow: "paic", versionId, roleKey });
      navigate(`/talent-mobility/candidate/${personId}/role/${roleId}?${params}`);
    },
    [navigate, versionId, roleKey, roleId]
  );

  const rows = useMemo(() => [...candidates].sort(bySmiDesc).map((candidate, index) => ({ ...candidate, rank: index + 1 })), [candidates]);

  const columns = useMemo(
    () => [
      { title: "#", key: "rank", dataIndex: "rank", width: 50 },
      ...buildPersonColumns(rows, {
        renderName: (_, record) => (
          <>
            {record.person?.name}
            {shortlistedIds.has(record.personId) && (
              <SbTag color="success" style={{ marginLeft: 8 }}>
                Shortlisted
              </SbTag>
            )}
          </>
        ),
      }),
      buildScoreColumn("SMI (this role)", "sri", thresholds, {
        index: "SMI",
        largeTag: true,
        onHeaderCell: () => ({ className: "tm-col-header-center" }),
      }),
      {
        title: "",
        key: "shortlistAction",
        align: "center",
        render: (_, record) => (
          <>
            {/* Secondary, like Workforce Planning's own View Profile / Shortlist. */}
            <SbButton
              buttonType="secondary"
              size="small"
              onClick={(event) => {
                event.stopPropagation();
                openProfile(record.personId);
              }}
            >
              View Profile / Shortlist
            </SbButton>
          </>
        ),
      },
    ],
    [rows, thresholds, shortlistedIds, openProfile]
  );

  const neededPositions = Math.max((targetPositions ?? 0) - (currentPositions ?? 0), 0);

  const handleExportCsv = () => {
    exportRowsAsCsv(
      rows.map((record) => ({
        Role: title,
        "Employee ID": record.person.employeeId || "—",
        Person: record.person.name,
        "SMI %": record.sri ?? "—",
        Shortlisted: shortlistedIds.has(record.personId) ? "Yes" : "No",
      })),
      `paic-org-redesign-candidates-${roleId}`
    );
  };

  return (
    <PageTransitionStyled>
      <PageHeader
        title={<>{title} — Candidates</>}
        titleExtra={planTag}
        subtitle={
          <Text type="secondary">
            People who could fill this role, ranked by <IndexInfoLabel index="SMI" />.
          </Text>
        }
        onBack={onBack}
        backLabel="Back to PAIC Org Redesign"
        actions={
          <>
            <SbButton
              buttonType="secondary"
              icon={<FileExcelOutlined />}
              disabled={isLoading}
              onClick={handleExportCsv}
            >
              Export CSV
            </SbButton>
            {planActions}
          </>
        }
      />

      <InfoCardStyled>
        <InfoStatStyled>
          <span className="label">Current Positions</span>
          <span className="value">{currentPositions ?? "—"}</span>
        </InfoStatStyled>
        <InfoStatStyled>
          <span className="label">Target Positions</span>
          <span className="value">{targetPositions ?? "—"}</span>
        </InfoStatStyled>
        <InfoStatStyled>
          <span className="label">At Needed</span>
          <span className="value">{neededPositions}</span>
        </InfoStatStyled>
      </InfoCardStyled>

      <ReductionTableWrapperStyled>
        <SbTable
          columns={columns}
          dataSource={rows}
          rowKey="personId"
          pagination={false}
          sticky
          // While loading, a spinner inside the table's empty row (see tableEmptyLocale).
          locale={tableEmptyLocale(isLoading ? null : rows, "No candidates profiled against this role yet.")}
        />
      </ReductionTableWrapperStyled>
    </PageTransitionStyled>
  );
};

export default PaicRoleCandidatesV2;
